import { useState } from 'react';
import { epochMsOf, instantFromISOString, systemClock } from '@sakhtar/calendar';
import { Icon, type SakhtarIconName } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type {
  NotificationItem,
  NotificationListResponse,
  NotificationType,
} from '../../lib/types/topbar.js';

const TYPE_PRESENTATION: Record<
  NotificationType,
  { readonly icon: SakhtarIconName; readonly tone: 'info' | 'warn' | 'success' }
> = {
  reminder: { icon: 'calendar', tone: 'info' },
  invitation: { icon: 'user', tone: 'info' },
  access: { icon: 'shield', tone: 'warn' },
  subscription: { icon: 'subscription', tone: 'info' },
  operational: { icon: 'check', tone: 'success' },
};

function relativeTimeFa(iso: string): string {
  let ms: number;
  try {
    ms = epochMsOf(systemClock.now()) - epochMsOf(instantFromISOString(iso));
  } catch {
    return '';
  }
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'اکنون';
  if (min < 60) return `${min.toLocaleString('fa-IR')} دقیقه پیش`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr.toLocaleString('fa-IR')} ساعت پیش`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'دیروز';
  return `${day.toLocaleString('fa-IR')} روز پیش`;
}

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html §02 اعلان‌ها.
 * Real backend throughout: GET/POST t/:slug/api/v1/notifications
 * (apps/api/src/notifications/notifications.controller.ts). No bulk
 * mark-all-read endpoint exists — "همه خوانده شد" calls the real per-item
 * endpoint for every currently-unread row (still one real mutation per
 * notification, not a fabricated bulk action).
 */
export function NotificationsPopover({ slug, open }: { slug: string; open: boolean }) {
  const base = tenantBasePath(slug);
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const [marking, setMarking] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const list = useApiGet<NotificationListResponse>(
    open
      ? `${base}/api/v1/notifications?${tab === 'unread' ? 'unreadOnly=true&' : ''}limit=20`
      : null,
    [tab, refreshKey],
  );

  async function markRead(id: string) {
    await apiPost(`${base}/api/v1/notifications/${id}/read`);
    setRefreshKey((k) => k + 1);
  }

  async function markAllRead(items: readonly NotificationItem[]) {
    const unread = items.filter((n) => !n.read);
    if (unread.length === 0) return;
    setMarking(true);
    try {
      await Promise.all(
        unread.map((n) => apiPost(`${base}/api/v1/notifications/${n.notificationId}/read`)),
      );
      setRefreshKey((k) => k + 1);
    } finally {
      setMarking(false);
    }
  }

  return (
    <div>
      <div
        style={{
          padding: '12px 14px 10px',
          borderBottom: '1px solid var(--sk-divider)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>اعلان‌ها</div>
          {list.status === 'ready' && list.data.notifications.some((n) => !n.read) ? (
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--sk-brand-600)',
                cursor: marking ? 'default' : 'pointer',
                opacity: marking ? 0.6 : 1,
              }}
              onClick={() => !marking && markAllRead(list.data.notifications)}
            >
              همه خوانده شد
            </span>
          ) : null}
        </div>
        <div className="sk-tabs" style={{ alignSelf: 'flex-start' }}>
          <button className="sk-tab" aria-selected={tab === 'all'} onClick={() => setTab('all')}>
            همه
          </button>
          <button
            className="sk-tab"
            aria-selected={tab === 'unread'}
            onClick={() => setTab('unread')}
          >
            خوانده‌نشده
          </button>
        </div>
      </div>

      <div style={{ maxHeight: 320, overflowY: 'auto', padding: 6 }}>
        {list.status === 'loading' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 10 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div
                  className="sk-skel"
                  style={{ width: 30, height: 30, borderRadius: 9, flex: '0 0 auto' }}
                />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <div className="sk-skel" style={{ height: 10, width: '78%' }} />
                  <div className="sk-skel" style={{ height: 8, width: '52%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {list.status === 'error' ? (
          <div style={{ padding: '20px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="sk-alert sk-alert--danger">
              <Icon name="warn" size="md" />
              <div className="sk-alert__body">
                <div className="sk-alert__title">دریافت اعلان‌ها ناموفق بود</div>
                <div style={{ fontSize: 12 }}>اتصال را بررسی کنید و دوباره تلاش کنید.</div>
              </div>
            </div>
            <button
              className="sk-btn sk-btn--secondary sk-btn--sm"
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              تلاش دوباره
            </button>
          </div>
        ) : null}

        {list.status === 'ready' && list.data.notifications.length === 0 ? (
          <div
            style={{
              padding: '34px 16px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{ color: 'var(--sk-fg-subtle)' }}>
              <Icon name="bell" size="lg" />
            </span>
            <span style={{ fontSize: 12, color: 'var(--sk-fg-subtle)' }}>
              {tab === 'unread' ? 'اعلان خوانده‌نشده‌ای ندارید.' : 'اعلان جدیدی ندارید.'}
            </span>
          </div>
        ) : null}

        {list.status === 'ready'
          ? list.data.notifications.map((n) => {
              const presentation = TYPE_PRESENTATION[n.type];
              return (
                <div
                  key={n.notificationId}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: 10,
                    borderRadius: 10,
                    cursor: 'pointer',
                  }}
                  onClick={() => !n.read && markRead(n.notificationId)}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      flex: '0 0 auto',
                      borderRadius: 9,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `color-mix(in oklch, var(--sk-${presentation.tone}), transparent 88%)`,
                      color: `var(--sk-${presentation.tone})`,
                    }}
                  >
                    <Icon name={presentation.icon} size="sm" />
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>
                      {n.title}
                    </span>
                    {n.actor?.name ? (
                      <span
                        style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '2px 0' }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            background: 'var(--sk-bg-subtle, #f1f5f9)',
                            color: 'var(--sk-fg-muted, #475569)',
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontWeight: 600,
                          }}
                        >
                          👤 {n.actor.name}
                          {n.actor.verb ? ` • ${n.actor.verb}` : ''}
                        </span>
                        {n.target?.entityRef ? (
                          <span
                            style={{
                              fontSize: 10,
                              background: 'var(--sk-brand-50, #eff6ff)',
                              color: 'var(--sk-brand-700, #1d4ed8)',
                              padding: '1px 6px',
                              borderRadius: 4,
                              fontFamily: 'monospace',
                            }}
                          >
                            {n.target.entityRef}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)', lineHeight: 1.6 }}>
                      {n.body}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                      {relativeTimeFa(n.createdAt)}
                    </span>
                  </span>
                  {!n.read ? (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: 'var(--sk-danger)',
                        flex: '0 0 auto',
                        marginTop: 6,
                      }}
                    />
                  ) : null}
                </div>
              );
            })
          : null}
      </div>

      <div
        style={{
          padding: 10,
          textAlign: 'center',
          borderTop: '1px solid var(--sk-divider)',
        }}
      >
        <a
          href={`${base}/mobile/notifications`}
          style={{ fontSize: 12, fontWeight: 500, color: 'var(--sk-brand-600)' }}
        >
          مشاهده همه اعلان‌ها
        </a>
      </div>
    </div>
  );
}
